import { Button, cn, ResponsiveTable, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import type { AgreementStatus, ServiceAgreementListItem } from './contractsMirror.js';
import {
  useAgreementInvoicesQuery,
  useAgreementStatusMutation,
  useAgreementsQuery,
  useArchiveAgreementMutation,
  useGenerateInvoiceMutation,
} from './useAgreementsQuery.js';

/**
 * The `/service-agreements/$id` detail route. Reads the agreement from the READ-ONLY coarse list
 * query (the carve exposes no separate detail endpoint this plan — the list carries the full row).
 * Renders the recurring schedule (frequency + next run), the linked job, and the THREE detail
 * actions:
 *   • the SINGLE explicit "Generate invoice" — the ONLY invoice-creating action (D-03),
 *   • "Pause" (non-destructive, a warning confirm),
 *   • "Cancel agreement" (destructive, a confirm: recurring invoicing stops, can't be undone).
 *
 * Pause/cancel route through `_money/ConfirmDialog` (focus-trapped, Esc-cancellable). Every color is
 * a locked `@hbs-us/ui` token (no hex, BRAND-06).
 */

const routeApi = getRouteApi('/service-agreements/$id');

type PendingAction = 'pause' | 'cancel' | 'archive' | null;

export function AgreementDetailRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fmt = useMoneyFormatters();
  const { id } = routeApi.useParams();
  const agreementId = Number(id);

  const query = useAgreementsQuery({});
  const statusMutation = useAgreementStatusMutation();
  const generateMutation = useGenerateInvoiceMutation();
  const archiveMutation = useArchiveAgreementMutation();
  const invoicesQuery = useAgreementInvoicesQuery(agreementId);
  const [pending, setPending] = useState<PendingAction>(null);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  const agreement: ServiceAgreementListItem | undefined = (query.data?.agreements ?? []).find(
    (a) => a.id === agreementId,
  );

  if (query.isError || !agreement) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('serviceAgreements:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">
          {t('serviceAgreements:error.body')}
        </p>
        <Link
          to="/service-agreements"
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('serviceAgreements:actions.backToList')}
        </Link>
      </section>
    );
  }

  const isActive = agreement.status === 'active';
  const isPaused = agreement.status === 'paused';

  const applyStatus = (status: AgreementStatus) => {
    statusMutation.mutate({ agreementId, status });
    setPending(null);
  };

  const applyArchive = () => {
    archiveMutation.mutate(
      { agreementId },
      { onSuccess: () => void navigate({ to: '/service-agreements' }) },
    );
    setPending(null);
  };

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <Link
        to="/service-agreements"
        className="mb-4 inline-flex text-muted-foreground text-sm underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t('serviceAgreements:actions.backToList')}
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {agreement.serviceName}
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            {agreement.customerName ?? t('serviceAgreements:table.noCustomer')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => generateMutation.mutate({ agreementId })}
            disabled={!isActive || generateMutation.isPending}
          >
            {t('serviceAgreements:actions.generateInvoice')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/service-agreements/$id/edit" params={{ id: String(agreementId) }}>
              {t('serviceAgreements:actions.edit')}
            </Link>
          </Button>
          {isActive ? (
            <Button variant="secondary" onClick={() => setPending('pause')}>
              {t('serviceAgreements:actions.pause')}
            </Button>
          ) : null}
          {isPaused ? (
            <Button
              variant="secondary"
              onClick={() => applyStatus('active')}
              disabled={statusMutation.isPending}
            >
              {t('serviceAgreements:actions.resume')}
            </Button>
          ) : null}
          {agreement.status !== 'cancelled' ? (
            <Button variant="destructive" onClick={() => setPending('cancel')}>
              {t('serviceAgreements:actions.cancel')}
            </Button>
          ) : null}
          <Button
            variant="destructive"
            onClick={() => setPending('archive')}
            disabled={archiveMutation.isPending}
          >
            {t('serviceAgreements:actions.archive')}
          </Button>
        </div>
      </header>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DetailField label={t('serviceAgreements:detail.status')}>
          {t(`serviceAgreements:status.${agreement.status}`)}
        </DetailField>
        <DetailField label={t('serviceAgreements:detail.amount')}>
          <span className="font-mono">{fmt.formatCurrency(agreement.amount)}</span>
        </DetailField>
        <DetailField label={t('serviceAgreements:detail.frequency')}>
          {t(`serviceAgreements:frequency.${agreement.billingFrequency}`, {
            defaultValue: agreement.billingFrequency,
          })}
        </DetailField>
        <DetailField label={t('serviceAgreements:detail.nextRun')}>
          <span className="font-mono">{agreement.nextBillingDate}</span>
        </DetailField>
        <DetailField label={t('serviceAgreements:detail.startDate')}>
          <span className="font-mono">{agreement.startDate}</span>
        </DetailField>
        <DetailField label={t('serviceAgreements:detail.linkedJob')}>
          {agreement.jobName ?? t('serviceAgreements:detail.noJob')}
        </DetailField>
      </dl>

      <section className="mt-8" aria-labelledby="generated-invoices-heading">
        <h2
          id="generated-invoices-heading"
          className="mb-3 font-display font-semibold text-foreground text-xl"
        >
          {t('serviceAgreements:invoices.heading')}
        </h2>
        {invoicesQuery.isPending ? (
          <div className="flex items-center justify-center rounded-md border border-border bg-card p-8">
            <Spinner label={t('common:status.loading')} />
          </div>
        ) : invoicesQuery.isError ? (
          <p
            role="alert"
            className="rounded-md border border-border bg-card p-6 text-muted-foreground text-sm"
          >
            {t('serviceAgreements:invoices.error')}
          </p>
        ) : (invoicesQuery.data?.invoices ?? []).length === 0 ? (
          <p className="rounded-md border border-border bg-card p-6 text-muted-foreground text-sm">
            {t('serviceAgreements:invoices.empty')}
          </p>
        ) : (
          <ResponsiveTable className="rounded-md border border-border bg-card">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">{t('serviceAgreements:invoices.heading')}</caption>
              <thead>
                <tr className="border-border border-b text-muted-foreground">
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('serviceAgreements:invoices.number')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('serviceAgreements:invoices.dateIssued')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('serviceAgreements:invoices.status')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-medium">
                    {t('serviceAgreements:invoices.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(invoicesQuery.data?.invoices ?? []).map((invoice) => (
                  <tr key={invoice.id} className="border-border border-b last:border-b-0">
                    <td className="px-4 py-3 font-medium text-foreground">
                      {invoice.invoiceNumber}
                    </td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">
                      {invoice.dateIssued}
                    </td>
                    <td className="px-4 py-3 text-foreground">{invoice.status}</td>
                    <td className="px-4 py-3 text-right font-mono text-foreground">
                      {fmt.formatCurrency(invoice.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        )}
      </section>

      <ConfirmDialog
        open={pending === 'pause'}
        heading={t('serviceAgreements:confirm.pauseHeading')}
        body={t('serviceAgreements:confirm.pauseBody')}
        confirmLabel={t('serviceAgreements:actions.pause')}
        cancelLabel={t('serviceAgreements:actions.dismiss')}
        confirmPending={statusMutation.isPending}
        onConfirm={() => applyStatus('paused')}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending === 'cancel'}
        heading={t('serviceAgreements:confirm.cancelHeading')}
        body={t('serviceAgreements:confirm.cancelBody')}
        confirmLabel={t('serviceAgreements:actions.cancel')}
        cancelLabel={t('serviceAgreements:actions.dismiss')}
        confirmPending={statusMutation.isPending}
        onConfirm={() => applyStatus('cancelled')}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending === 'archive'}
        heading={t('serviceAgreements:confirm.archiveHeading')}
        body={t('serviceAgreements:confirm.archiveBody')}
        confirmLabel={t('serviceAgreements:actions.archive')}
        cancelLabel={t('serviceAgreements:actions.dismiss')}
        confirmPending={archiveMutation.isPending}
        onConfirm={applyArchive}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={cn('rounded-md border border-border bg-card p-6')}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 font-medium text-foreground">{children}</dd>
    </div>
  );
}
